import utils from 'lib/utils';
import { KeybindingService, keys } from 'services/event-handler-services/keybinding-service';
const { Tab } = keys;

const FocusService = function () {
  let focusNodes = [];
  let inertNodes = [];

  /**
   * Traps keyboard focus within a specified element given a selector
   * @param {*} view the view itself
   * @param {*} trapSelector the selector for the container where we want the keyboard to trap
   * @param {*} containerSelector the container selector where we want to ignore the focus
   * @param {string} [focusableElements] optional comma-separated CSS selectors to append to the default focusable elements (e.g. 'h1,h3')
   * @param {Object} [options] optional configuration
   * @param {boolean} [options.autoFocus=true] auto-focus the first focusable element
   */
  this.trapFocus = (view, trapSelector, containerSelector = '#site-container', focusableElements, options = {}) => {
    const { autoFocus = true } = options;
    const inertSelector = focusableElements
      ? `${utils.focusableElements()},${focusableElements.trim()}`
      : utils.focusableElements();
    const el = view.el;
    focusNodes = this.getFocusNodes(el, inertSelector);
    const { first, last } = this.getFirstLastNodes(focusNodes);
    this.inert(inertSelector, containerSelector, focusNodes, el);
    this.trapKeyboard(view, trapSelector, first, last, autoFocus);
  };

  /**
   * Gets the first and last nodes given a node list
   * @param {*} nodeList list of the nodes
   * @returns returns first and last node in the node list
   */
  this.getFirstLastNodes = nodeList => {
    const firstNode = nodeList[0];
    const tentativeLastNode = nodeList[nodeList.length - 1];

    // if there is only 1 focusable node, then the last node is the first node
    const lastNode = tentativeLastNode ?? firstNode;

    return {
      first: firstNode,
      last: lastNode,
    };
  };

  /**
   * Returns elements within a given element and selector
   * @param {*} el DOM element in which elements are needed
   * @param {*} selector Selector for the nodes that we want to find
   * @returns
   */
  this.getFocusNodes = (el, selector) => {
    // guard against undefined querySelector
    if (!el || !selector) {
      return [];
    }

    const focusableElements = el.querySelectorAll(selector);
    // grab focusable nodes within a modal
    return Array.from(focusableElements);
  };

  /**
   * Inert makes keyboard navigation ignore focusable elements outside of a modal when a modal is open
   * TODO: look into using the actual inert polyfill
   * Inspired by   - https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/inert
   *               - https://zellwk.com/blog/keyboard-focusable-elements/
   *
   * @param {*} iSelector Focusable elements selector
   * @param {*} containerSelector the container selector where we want to ignore the focus
   * @param {*} fNodes Focusable nodes
   * @param {*} el Element
   * @returns
   */
  this.inert = (iSelector, containerSelector = '#site-container', fNodes = [], el = []) => {
    /**
     * Grab all focusable elements in the site-container.
     * We need to store the nodes so that they to be used when the modal is closed to reset the focus.
     */
    const nodesToInert = this.getNodesToInert(containerSelector, el, iSelector, fNodes);

    // Using for loop for performance
    for (let i = 0; i < nodesToInert.length; i++) {
      const node = nodesToInert[i];
      if (!inertNodes.includes(node)) {
        node.prevTabindex = node.getAttribute('tabindex');
        node.setAttribute('tabindex', -1);
        node.style.outline = 'none';
        inertNodes.push(node);
      }
    }

    return nodesToInert;
  };

  /**
   * Return nodes to ignore during keyboard trap
   * @param {*} containerSelector the container selector where we want to ignore the focus
   * @param {*} el Element
   * @param {*} iSelector Focusable elements selector
   * @param {*} fNodes Focusable nodes
   * @returns
   */
  this.getNodesToInert = (containerSelector, el, iSelector, fNodes) => {
    // guard against undefined querySelectors
    if ((!containerSelector && !el) || !iSelector) {
      return [];
    }

    const container = document.querySelector(containerSelector) ?? el;

    // guard against undefined container -- sdk -> test failure
    if (!container || container.length === 0) {
      return [];
    }

    return Array.from(container.querySelectorAll(iSelector))?.filter?.(node => !fNodes?.includes?.(node) ?? []);
  };

  this.releaseFocus = () => {
    if (!inertNodes.length) {
      return;
    }
    const nodesToReset = inertNodes;

    for (let i = 0; i < nodesToReset.length; i++) {
      const node = nodesToReset[i];
      if (node.prevTabindex) {
        node.setAttribute('tabindex', node.prevTabindex);
        node.prevTabindex = null;
      } else {
        node.removeAttribute('tabindex');
      }
      node.style.outline = null;
    }

    inertNodes = [];
  };

  this.trapKeyboard = (view, trapSelector, firstFocusableEl, lastFocusableEl, autoFocus = true) => {
    if (autoFocus) {
      setTimeout(() => {
        firstFocusableEl?.focus();
      }, 0);
    }

    if (this.keyboardTrap) {
      view.undelegate('keydown', this.keyboardTrap);
    }
    this.keyboardTrap = view.delegate('keydown', trapSelector, e => {
      KeybindingService.with(e).bind(Tab, () => {
        if (e.shiftKey && document.activeElement === firstFocusableEl) {
          lastFocusableEl?.focus();
          e.preventDefault();
        } else {
          const checkLastFocusable = element => {
            // check if element exists
            if (!element) {
              return;
            }

            const lastElement = focusNodes[focusNodes.length - 1];
            // check if the current focusNodes element is hidden
            if (element.clientWidth === 0 && element.clientHeight === 0) {
              const index = focusNodes.indexOf(element);
              const prevElement = focusNodes[index - 1];
              checkLastFocusable(prevElement);
            } else if (!e.shiftKey && document.activeElement === lastElement) {
              firstFocusableEl?.focus();
              e.preventDefault();
            }
          };

          checkLastFocusable(lastFocusableEl);
        }

        // for modal nav buttons and other buttons nested in containers
        if (!e.shiftKey && document.activeElement.id && document.activeElement.id === lastFocusableEl.id) {
          firstFocusableEl?.focus();
          e.preventDefault();
        }
      });
    });
  };
};

export default new FocusService();
